import React, { useCallback, useEffect, useState, useRef } from "react";
import Layout1 from "../../DataLayouts/Layout1";
import NextWeekIcon from "@mui/icons-material/NextWeek";
import FormatAlignJustifyIcon from "@mui/icons-material/FormatAlignJustify";
import { fetchEmployeeLeaves } from "../../../Apis/Employee-api";
import { MAIN_URL } from "../../../Configurations/Urls";
import DateRangeIcon from "@mui/icons-material/DateRange";
import PersonIcon from "@mui/icons-material/Person";
import CategoryIcon from "@mui/icons-material/Category";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import toast from "react-hot-toast";
import axios from "axios";
import TableDataGeneric from "../../../Configurations/TableDataGeneric";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Layout4 from "../../DataLayouts/Layout4";
import { fetchInternAttendanceTimeLogs } from "../../../Apis/InternManagement";
import { operatorRequiresValue } from "../../../Components/filters/operators";

const DEFAULT_COLUMNS = [
  {
    field: "intern_code",
    label: "intern_code",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "intern_name",
    label: "intern_name",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },

  {
    field: "attendance_date",
    label: "attendance_date",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },

  {
    field: "attendance_log_time",
    label: "attendance_log_time",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },

  {
    field: "attendance_log_type",
    label: "attendance_log_type",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "break_type",
    label: "Break Type",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
];

const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 1,
  hasNext: false,
  hasPrev: false,
};

const DEFAULT_URL_PARAMS = {
  page: "1",
  pageSize: "10",
  sortBy: "attendance_log_time",
  sortOrder: "desc",
};

// Helper functions
const buildFiltersFromParams = (searchParams) => {
  const filters = [];
  const processedFields = new Set();

  searchParams.forEach((value, key) => {
    if (
      key.endsWith("_operator") ||
      key.endsWith("_logic") ||
      !key.startsWith("filter_")
    )
      return;

    const field = key.replace("filter_", "");
    if (processedFields.has(field)) return;
    processedFields.add(field);

    const operator = searchParams.get(`filter_${field}_operator`) || "contains";
    const requiresValue = operatorRequiresValue(operator);

    if (!requiresValue || value) {
      let filterValue = requiresValue ? value : null;

      if (requiresValue && filterValue) {
        if (["equals", "not_equals"].includes(operator)) {
          filterValue = filterValue
            .split(",")
            .map((v) => v.trim().toLowerCase());
        } else if (["between", "not_between"].includes(operator)) {
          filterValue = filterValue.split(",").map((v) => v.trim());
        }
      }

      // Normalize display date like "Feb 27, 2026" to "2026-02-27" for attendance_date filters
      if (field === "attendance_date" && filterValue) {
        const normalizeOne = (val) => {
          const d = new Date(val);
          if (isNaN(d)) return val;
          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, "0");
          const day = String(d.getDate()).padStart(2, "0");
          return `${year}-${month}-${day}`;
        };

        if (Array.isArray(filterValue)) {
          filterValue = filterValue.map(normalizeOne);
        } else {
          filterValue = normalizeOne(filterValue);
        }
      }

      filters.push({ field, operator, value: filterValue });
    }
  });
  return filters;
};

function InternAttendanceTimelogsList() {
  const [leaves, setLeaves] = useState([]);
  const { userData } = useAuthStore();
  const org = userData?.organization;
  const [loading, setLoading] = useState(true);
  const [tableConfig, setTableConfig] = useState(null);
  const [configColumns, setConfigColumns] = useState(DEFAULT_COLUMNS);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [paginationData, setPaginationData] = useState(DEFAULT_PAGINATION);
  const [refreshKey, setRefreshKey] = useState(0);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fetchControllerRef = useRef(null);
  const hasInitializedURL = useRef(false);

  const { id } = useParams();

  const getFormattedAttendanceLogs = async (orgId) => {
    try {
      const data = await fetchInternAttendanceTimeLogs(orgId);
      const a = data?.attendance_time_logs || [];

      return a.map((item) => ({
        ...item,
        id: item.organization_person_attendance_timelog_id,
           intern_name :item?.intern_name,
          intern_code: item?.intern?.org_persons?.person_code,
          attendance_date: new Date(item.attendance_date).toLocaleDateString(
          "en-US",
          {
            year: "numeric",
            month: "short",
            day: "numeric",
          }
        ),
        intern_code: item?.intern?.intern_code,
        remarks: item?.remarks === null ? "" : item?.remarks,
        attendance_log_time: new Date(item.attendance_log_time).toLocaleString(
          "en-US",
          {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "numeric",
            second: "numeric",
            hour12: true,
          }
        ),
      }));
    } catch (err) {
      if (err.response && err.response.status === 401) {
        toast.error("Session Expired!");
        window.location.href = "/login";
      }
      console.error("Failed to fetch attendance logs:", err);
      return [];
    }
  };

  // Initialize URL parameters
  useEffect(() => {
    if (hasInitializedURL.current) return;

    const params = new URLSearchParams(searchParams);
    let needsUpdate = false;

    Object.entries(DEFAULT_URL_PARAMS).forEach(([key, defaultValue]) => {
      if (!params.has(key)) {
        params.set(key, defaultValue);
        needsUpdate = true;
      }
    });

    if (needsUpdate) {
      setSearchParams(params, { replace: true });
    }

    hasInitializedURL.current = true;
  }, [searchParams, setSearchParams]);

  // Load table configuration from general-datagrids API
  useEffect(() => {
    const loadTableConfiguration = async () => {
      if (!org?.organization_id) {
        setLoadingConfig(false);
        return;
      }

      try {
        const configRes = await fetch(
          `${MAIN_URL}/api/general-datagrids`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json" },
          }
        );

        if (configRes.ok) {
          const configResponse = await configRes.json();
          const datagrids = configResponse.datagrids || [];
          const orgKey = `intern_attendance_timelogs_grid_${org.organization_id}`;
          const savedConfig = datagrids.find(
            (dg) => dg.datagrid_key === orgKey
          );

          if (savedConfig) {
            const serverCfg = savedConfig.datagrid_default_configuration;
            setTableConfig(serverCfg);

            if (
              serverCfg?.columns &&
              Array.isArray(serverCfg.columns) &&
              serverCfg.columns?.length > 0
            ) {
              setConfigColumns(serverCfg.columns);
            } else {
              setConfigColumns(DEFAULT_COLUMNS);
            }
          } else {
            setConfigColumns(DEFAULT_COLUMNS);
          }
        }
      } catch (error) {
        console.error("Error loading table configuration:", error);
        setConfigColumns(DEFAULT_COLUMNS);
      } finally {
        setLoadingConfig(false);
      }
    };

    loadTableConfiguration();
  }, [org?.organization_id]);

  // load data
  useEffect(() => {
    if (!org?.organization_id || !hasInitializedURL.current) return;

    fetchControllerRef.current?.abort();
    fetchControllerRef.current = new AbortController();

    setLoading(true);

    const fetchData = async () => {
      try {
        const filters = buildFiltersFromParams(searchParams);
        const search = searchParams.get("search");
        const requestBody = {
          filters,
          page: Number.parseInt(searchParams.get("page"), 10) || 1,
          pageSize: Number.parseInt(searchParams.get("pageSize"), 10) || 10,
          sort: {
            field: searchParams.get("sortBy") || "attendance_log_time",
            direction: searchParams.get("sortOrder") || "desc",
          },
          ...(search && search.trim() !== "" && { search: search.trim() }),
        };

        // Try new POST endpoint first, fallback to old GET endpoint if needed
        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org.organization_id}/intern-time-logs-filtered`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
            signal: fetchControllerRef.current.signal,
          }
        );

        let data;
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            data = result.data;
            
            if (result.pagination) {
              const pageSize =
                result.pagination.pageSize || result.pagination.limit || 10;
              setPaginationData({
                page:
                  result.pagination.page ||
                  Number.parseInt(searchParams.get("page"), 10) ||
                  1,
                pageSize,
                total: result.pagination.total || 0,
                totalPages: result.pagination.totalPages || 1,
                hasNext: result.pagination.hasNext || false,
                hasPrev: result.pagination.hasPrev || false,
              });
            }
          } else {
            data = [];
            setPaginationData(DEFAULT_PAGINATION);
          }
        } else if (response.status === 404) {
          // Fallback to old GET endpoint if POST endpoint doesn't exist yet
          const logs = await getFormattedAttendanceLogs(org.organization_id);
          data = logs;
          setPaginationData(DEFAULT_PAGINATION);
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Transform data - preserve existing transformation logic
        const formattedLogs = data.map((item) => ({
          ...item,
          id: item.organization_person_attendance_timelog_id,
          intern_name:
            item?.intern_name || "-",
           
          intern_code: item?.intern_code|| "",
          attendance_date: item.attendance_date 
            ? new Date(item.attendance_date).toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
              })
            : "",
          remarks: item?.remarks === null ? "" : item?.remarks,
          attendance_log_time: item.attendance_log_time
            ? new Date(item.attendance_log_time).toLocaleString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "numeric",
                minute: "numeric",
                second: "numeric",
                hour12: true,
              })
            : "",
        }));
        
        setLeaves(formattedLogs);
      } catch (err) {
        if (err.name === "AbortError") return;
        
        if (err.response && err.response.status === 401) {
          toast.error("Session Expired!");
          window.location.href = "/login";
        } else {
          console.error("Failed to fetch attendance logs:", err);
          toast.error("Failed to fetch attendance logs");
          setLeaves([]);
          setPaginationData(DEFAULT_PAGINATION);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      fetchControllerRef.current?.abort();
    };
  }, [org, searchParams, refreshKey]);

const handleDelete = async (id) => {
  try {
    const response = await fetch(
      `${MAIN_URL}/api/organizations/${org?.organization_id}/intern-time-logs/${id}`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      }
    );

    const data = await response.json(); // ✅ Parse JSON manually

    if (response.ok) {
      toast.success(data.message || "Attendance log deleted successfully");
      // Always re-fetch so paired/cascaded deletes (e.g. Break End/Clock Out) disappear immediately.
      setRefreshKey((key) => key + 1);
      return;
    }

    // ❌ If not ok, throw to go into catch()
    throw new Error(data.error || data.message || "Failed to delete record");

  } catch (error) {
    console.error("Delete failed:", error);
    toast.error(error.message || "An unexpected error occurred while deleting.");
  }
};


  const handleEdit = useCallback(
    (item) => {
      navigate(`/intern/attendance/time-logs/edit/${item.id}`);
    },

    [navigate]
  );


               const handleShow = useCallback(
    (item) => {
      navigate(`/intern/attendance/time-logs/view/${item.id}`)
    },
    [navigate],
  )


  return (
    <>
      <Layout4
        loading={loading}
        heading={"Intern Attendance Time Logs"}
        btnName={"Add Time Log"}
        Data={leaves}
        delete_action={"INTERN_TIME_LOG_DELETE"}
        tableHeaders={[
          { name: "Intern Code", value_key: "intern_code" },
          { name: "Intern Name", value_key: "intern_name", textStyle: "capitalize" },
          { name: "Attendance Date", value_key: "attendance_date" },
          {
            name: "Attendance Log Type",
            value_key: "attendance_log_type",
            textStyle: "capitalize",
          },
          { name: "Break Type", value_key: "break_type", textStyle: "capitalize" },
          { name: "Attendance Log Time", value_key: "attendance_log_time" },
          { name: "Remarks", value_key: "remarks", textStyle: "capitalize" },
        ]}
        Icons={[
          <PersonIcon sx={{ fontSize: 60, color: "grey.500", mb: 2 }} />,
          <FormatAlignJustifyIcon color="primary" />,
          <CategoryIcon sx={{ color: "text.secondary" }} />,
          <DateRangeIcon sx={{ color: "text.secondary" }} />,
        ]}
        messages={[
          "Attendance Time Logs",
          "Attendance Time Logs",
          "Add Time Logs",
          "Attendance Time Logs",
        ]}
        Route={"/intern/attendance/time-logs"}
        setData={setLeaves}
        DeleteFunc={handleDelete}
      />

      {!loadingConfig && (
        <TableDataGeneric
          tableName="intern_attendance_timelogs"
          primaryKey="organization_person_attendance_timelog_id"
          heading="Intern Attendance Time Logs"
          data={leaves}
          sortname="attendance_log_time"
          edit_delete_action={["INTERN_TIME_LOG_EDIT", "INTERN_TIME_LOG_DELETE"]}
          showActions={true}
          Route="/intern/attendance/time-logs"
          DeleteFunc={handleDelete}
          handleShow={handleShow}
          EditFunc={handleEdit}
          token={localStorage.getItem("token")}
          configss={configColumns}
          paginationData={paginationData}
          filterModule="intern_attendance_timelogs"
          {...(tableConfig && { config: tableConfig })}
        />
      )}
    </>
  );
}

export default InternAttendanceTimelogsList;

